import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Table, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import VereinbarungService from '../services/vereinbarung.service';

const EMPTY_TITEL = { titel_id: null, titel: '', beschreibung: '', inhalt_vorlage: '', is_system: false };

// Lesbare Labels fuer die Stufen-Codes (muss mit Backend-Whitelist uebereinstimmen).
const TIER_LABELS = { mainvo: 'Haupt-VO', subvo: 'SubVo', adm: 'ADM', customer: 'Kunde' };

const BoardVereinbarungstitel = ({ scope = 'stage' }) => {
    // System-Modus: Admin verwaltet wiederverwendbare System-Vereinbarungsvorlagen.
    const isSystem = scope === 'system';
    const navigate = useNavigate();
    const [titelList, setTitelList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // State für das Modal (Popup-Fenster)
    const [showModal, setShowModal] = useState(false);
    const [currentTitel, setCurrentTitel] = useState({ ...EMPTY_TITEL });
    const [isEditMode, setIsEditMode] = useState(false);

    // Funktion zum Laden der Daten vom Server
    const fetchTitel = () => {
        setLoading(true);
        VereinbarungService.getAllAgreementTitles()
            .then(response => {
                setTitelList(response.data);
                setLoading(false);
            })
            .catch(err => {
                setError('Fehler beim Laden der Titel.');
                setLoading(false);
            });
    };

    // Daten beim ersten Laden der Komponente abrufen
    useEffect(() => {
        fetchTitel();
    }, []);

    // Modal-Funktionen
    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentTitel({ ...EMPTY_TITEL });
        setIsEditMode(false);
    };

    const handleShowCreateModal = () => {
        // System-Vorlagen werden in der vollwertigen Maske (Look der echten
        // "Neue Vereinbarung erstellen") bearbeitet, nicht im Mini-Modal.
        if (isSystem) {
            navigate('/system-agreement-templates/new');
            return;
        }
        setIsEditMode(false);
        setCurrentTitel({ ...EMPTY_TITEL });
        setShowModal(true);
    };

    const handleShowEditModal = (titel) => {
        if (isSystem) {
            navigate(`/system-agreement-templates/${titel.titel_id}`);
            return;
        }
        setIsEditMode(true);
        setCurrentTitel({
            ...EMPTY_TITEL,
            ...titel,
            inhalt_vorlage: titel.inhalt_vorlage || '',
            is_system: !!titel.is_system,
        });
        setShowModal(true);
    };

    // Speicher-Funktion
    const handleSave = () => {
        setError('');
        // Im System-Modus wird die Vorlage als System-Vorlage markiert.
        const payload = {
            titel: currentTitel.titel,
            beschreibung: currentTitel.beschreibung,
            inhalt_vorlage: currentTitel.inhalt_vorlage || null,
            ...(isSystem ? { is_system: true } : {}),
        };
        const promise = isEditMode
            ? VereinbarungService.updateAgreementTitle(currentTitel.titel_id, payload)
            : VereinbarungService.createAgreementTitle(payload);

        promise
            .then(() => {
                fetchTitel(); // Liste neu laden
                handleCloseModal();
            })
            .catch(err => {
                const msg = (err.response && err.response.data && err.response.data.message) || 'Fehler beim Speichern des Titels.';
                setError(msg);
            });
    };
    
    // Löschen-Funktion
    const handleDelete = (id) => {
        // Sicherheitsabfrage
        if (window.confirm('Sind Sie sicher, dass Sie diesen Titel löschen möchten?')) {
            VereinbarungService.deleteAgreementTitle(id)
                .then(() => {
                    fetchTitel(); // Liste neu laden
                })
                .catch(err => {
                    const msg = (err.response && err.response.data && err.response.data.message) || 'Fehler beim Löschen des Titels.';
                    setError(msg);
                });
        }
    };

    return (
        <Container>
            <div className="d-flex justify-content-between align-items-center my-3">
                <h1>{isSystem ? 'System-Vereinbarungsvorlagen verwalten' : 'Vereinbarungstitel verwalten'}</h1>
                <Button variant="success" onClick={handleShowCreateModal}>
                    {isSystem ? '+ Neue System-Vorlage anlegen' : '+ Neuen Titel anlegen'}
                </Button>
            </div>

            {isSystem && (
                <Alert variant="info">
                    System-Vorlagen kombinieren einen Titel mit einem wiederverwendbaren Standard-Inhalt (inkl. Platzhalter).
                    Stufen können diese Vorlage bei der Vereinbarungserstellung als Ausgangspunkt übernehmen.
                </Alert>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Titel</th>
                            <th>Beschreibung</th>
                            <th className="text-center" style={{ width: '120px' }}>Inhaltsvorlage</th>
                            {isSystem && <th style={{ width: '180px' }}>Sichtbar in</th>}
                            <th style={{ width: '150px' }}>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {titelList.map(titel => {
                            // System-Vorlagen sind in den Stufen sichtbar, aber nur lesbar.
                            const isSystemRow = !isSystem && titel.is_system === true;
                            const hasContent = !!(titel.inhalt_vorlage && String(titel.inhalt_vorlage).trim());
                            return (
                            <tr key={titel.titel_id}>
                                <td>{titel.titel_id}</td>
                                <td>
                                    {titel.titel}
                                    {isSystemRow && <Badge bg="secondary" className="ms-2">System</Badge>}
                                </td>
                                <td>{titel.beschreibung}</td>
                                <td className="text-center align-middle">
                                    {hasContent
                                        ? <Badge bg="success">vorhanden</Badge>
                                        : <span className="text-muted small">—</span>}
                                </td>
                                {isSystem && (
                                    <td>
                                        {Array.isArray(titel.visible_tiers) && titel.visible_tiers.length
                                            ? titel.visible_tiers.map((t) => (
                                                <Badge bg="info" text="dark" className="me-1" key={t}>{TIER_LABELS[t] || t}</Badge>
                                              ))
                                            : <span className="text-muted small">keine</span>}
                                    </td>
                                )}
                                <td>
                                    {isSystemRow ? (
                                        <span className="text-muted small">System-Vorlage (nur lesbar)</span>
                                    ) : (
                                        <>
                                            <Button variant="primary" size="sm" onClick={() => handleShowEditModal(titel)} className="me-2" title="Bearbeiten" aria-label="Bearbeiten">
                                                <FaEdit />
                                            </Button>
                                            <Button variant="danger" size="sm" onClick={() => handleDelete(titel.titel_id)} title="Löschen" aria-label="Löschen">
                                                <FaTrash />
                                            </Button>
                                        </>
                                    )}
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}

            {/* Modal für Erstellen/Bearbeiten */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEditMode
                            ? (isSystem ? 'System-Vorlage bearbeiten' : 'Titel bearbeiten')
                            : (isSystem ? 'Neue System-Vorlage anlegen' : 'Neuen Titel anlegen')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Titel</Form.Label>
                            <Form.Control
                                type="text"
                                value={currentTitel.titel}
                                onChange={e => setCurrentTitel({ ...currentTitel, titel: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Beschreibung</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={currentTitel.beschreibung}
                                onChange={e => setCurrentTitel({ ...currentTitel, beschreibung: e.target.value })}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Inhaltsvorlage (wiederverwendbar)</Form.Label>
                            <ReactQuill
                                theme="snow"
                                value={currentTitel.inhalt_vorlage || ''}
                                onChange={(val) => setCurrentTitel({ ...currentTitel, inhalt_vorlage: val })}
                            />
                            <Form.Text className="text-muted">
                                Standard-Inhalt inkl. Platzhalter (z.&nbsp;B. {'{{kunde.name}}'}). Stufen können diesen
                                Inhalt bei der Vereinbarungserstellung als Ausgangspunkt übernehmen.
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>
                        Abbrechen
                    </Button>
                    <Button variant="primary" onClick={handleSave}>
                        Speichern
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default BoardVereinbarungstitel;